import Bugsnag from "@bugsnag/js";
import { CalloutCard, Card, EmptyState, Heading, Pagination, ResourceList, Stack, TextStyle } from "@shopify/polaris";
import React, { useCallback, useEffect, useState } from "react";
import { View } from "react-native";
import { AdListingItemStatus, ListAdListingsItems_listAdListingItems, ListAdListingsItems_listAdListingItems_edges_node } from "../../../api/spacex.types";
import { useClient } from "../../../api/useClient";
import { ResourceListSkeleton } from "../../../components/Skeleton/ResourceListSkeleton";
import { ReviewListingItemModal } from "./ReviewListingItemModal";
import { TargetedAdLisingItem } from "./TargetedAdLisingItem";

const notificationsIcon = require('../../../assets/images/notificatios-icon.svg');

export const IncomingAdListingItems = React.memo(() => {
    const client = useClient();
    const [results, setResults] = useState<ListAdListingsItems_listAdListingItems>();
    const [loading, setLoading] = useState<boolean>(false);
    const [reviewModalItem, setReviewModalItem] = useState<ListAdListingsItems_listAdListingItems_edges_node>();
    const pageSize = 6;

    const loadPage = useCallback((pageSize: number, after?: string | null) => {
        client.queryListAdListingsItems({
            first: pageSize,
            after: after,
            status: AdListingItemStatus.INACTIVE
        })
            .then((result) => {
                console.log('Nex page loaded', result);
                setResults(result.listAdListingItems);
                setLoading(false);
            })
            .catch((e) => {
                console.log(e);
                setLoading(false);
            });
    }, [client]);

    const loadNextPage = useCallback(() => {
        console.log('loading next page')
        setLoading(true);
        loadPage(pageSize, results?.pageInfo.endCursor);
    }, [results, loadPage]);

    const loadPrevPage = useCallback(() => {
        if (!results?.pageInfo.hasPreviousPage) {
            throw new Error("No previous page");
        }
        setLoading(true);
        loadPage(pageSize, results.pageInfo.startCursor);
    }, [pageSize, results, loadPage]);

    useEffect(() => {
        loadNextPage();
    }, []);

    const handleModalCancel = useCallback(
        () => {
            setReviewModalItem(undefined);
        },
        [setReviewModalItem],
    );

    const handleAcceptListingItem = useCallback(
        (listing?: ListAdListingsItems_listAdListingItems_edges_node) => {
            if (listing) {
                client.mutateAcceptTargetedListing({ id: listing.id }).then((res) => {
                    console.log('mutateAcceptCommunityListing', res);
                }).catch((e) => {
                    console.log('mutateAcceptCommunityListing error', e);
                    Bugsnag.notify(e);
                }).finally(() => {
                    handleModalCancel();
                });
            }
        },
        [client, handleModalCancel],
    );

    if (loading) {
        return (<ResourceListSkeleton />);
    }

    return (
        <Card>
            <Card.Section>
                <View style={{flexDirection: 'row'}}>
                    <Stack.Item fill>
                        <Stack vertical>
                            <Stack.Item>
                                <Heading>
                                    <TextStyle variation={'strong'}>
                                        {'Requests addressed to you'}
                                    </TextStyle>
                                </Heading>
                            </Stack.Item>
                            <Stack.Item>
                                <TextStyle variation={'subdued'}>
                                    {
                                        'Positions that are requested to be placed on your Cartmate storefront from Cartmate community stores. \n'
                                        + 'You can accept or reject requests and tag store as friendly (all requests from friendly stores are accepted automatically).'
                                    }
                                </TextStyle>
                            </Stack.Item>
                        </Stack>
                    </Stack.Item>
                    <Stack.Item>
                        <div style={{
                            textAlign: 'center',
                            width: '100px',
                            height: '100px',
                            backgroundImage: `url(${notificationsIcon})`,
                            backgroundSize: 'cover',
                        }} />
                    </Stack.Item>
                        </View>
            </Card.Section>
            <Card.Section>
                {(results?.edges.length === 0) && <EmptyState
                    heading="No requests yet"
                    image="https://cdn.shopify.com/s/files/1/2376/3301/products/emptystate-files.png" />}
                {(results?.edges && results?.edges.length > 0) && (
                    <ResourceList
                        items={results?.edges.map((e) => e.node)}
                        renderItem={(l) => {
                            return (
                                <ResourceList.Item
                                    onClick={() => { }}
                                    id={l.adListing.id}
                                    shortcutActions={[
                                        {
                                            content: 'Review',
                                            onAction: () => {
                                                setReviewModalItem(l);
                                            }
                                        }
                                    ]}
                                >
                                    <TargetedAdLisingItem item={l} />
                                </ResourceList.Item>
                            );
                        }}
                    />
                )}
            </Card.Section>
            {(results?.pageInfo.hasNextPage || results?.pageInfo.hasPreviousPage) && <Card.Section>
                <Stack distribution={"center"}>
                    <Pagination
                        hasPrevious={results?.pageInfo.hasPreviousPage}
                        onPrevious={() => {
                            loadPrevPage()
                        }}
                        hasNext={results?.pageInfo.hasNextPage}
                        onNext={() => {
                            loadNextPage()
                        }}
                    />
                </Stack>
            </Card.Section>}
            <ReviewListingItemModal
                item={reviewModalItem}
                handleAccept={handleAcceptListingItem}
                handleCancel={handleModalCancel}
            />
        </Card >
    );
});